import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorNote, Field, inputClass } from '../components/ui';
import { errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth-context-value';
import { apiAssetUrl, invitations } from '../lib/resources';

/**
 * Link de convite (/convite/:token) — público. Com conta existente, pede só a
 * senha dela; sem conta, cria a conta com o e-mail do convite.
 */
export function AcceptInvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { startSession } = useAuth();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');

  const preview = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => invitations.preview(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () =>
      invitations.accept(token, {
        password,
        name: preview.data?.userExists ? undefined : name,
      }),
    onSuccess: (session) => {
      startSession(session);
      navigate('/', { replace: true });
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    accept.mutate();
  };

  const data = preview.data;
  const logo = apiAssetUrl(data?.workspace.logoUrl);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-lg shadow-slate-200">
        {preview.isLoading && <p className="text-sm text-slate-500">Abrindo o convite…</p>}

        {preview.error && (
          <>
            <h1 className="text-xl font-semibold text-slate-900">Convite indisponível</h1>
            <p className="mt-2 text-sm text-slate-500">
              {errorMessage(preview.error, 'Este convite não existe mais.')}
            </p>
          </>
        )}

        {data && (
          <form onSubmit={submit} className="space-y-4">
            {logo ? (
              <img src={logo} alt="" className="h-10 w-10 rounded-xl object-contain" />
            ) : (
              <div
                className="h-10 w-10 rounded-xl"
                style={{ backgroundColor: 'var(--crm-primary)' }}
              />
            )}
            <div>
              <h1 className="text-xl font-semibold text-slate-900">
                Entrar em {data.workspace.name}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Convite para <strong>{data.email}</strong>
                {data.isAdmin && ' como administrador'}.
              </p>
            </div>

            {!data.userExists && (
              <Field label="Seu nome">
                <input
                  required
                  minLength={2}
                  autoComplete="name"
                  className={inputClass}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </Field>
            )}
            <Field
              label={data.userExists ? 'Senha da sua conta' : 'Crie uma senha'}
              hint={
                data.userExists
                  ? 'Você já tem conta com este e-mail.'
                  : 'Mínimo de 8 caracteres.'
              }
            >
              <input
                required
                type="password"
                minLength={data.userExists ? 1 : 8}
                autoComplete={data.userExists ? 'current-password' : 'new-password'}
                className={inputClass}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            {accept.error && (
              <ErrorNote>{errorMessage(accept.error, 'Não foi possível aceitar')}</ErrorNote>
            )}

            <button
              type="submit"
              disabled={accept.isPending}
              className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition disabled:opacity-60"
              style={{
                backgroundColor: 'var(--crm-primary)',
                color: 'var(--crm-primary-foreground)',
              }}
            >
              {accept.isPending ? 'Entrando…' : 'Aceitar convite'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
